import React, { useState } from 'react';
import { Eye, EyeOff, Key, Mail, MessageSquare, CreditCard, Shield } from 'lucide-react';

const IntegrationKeysStep = ({ formData, updateFormData, errors = {}, clearError }) => {
    const [showRazorpaySecret, setShowRazorpaySecret] = useState(false);
    const [showWhatsappKey, setShowWhatsappKey] = useState(false);

    const updateIntegrationKey = (key, value) => {
        updateFormData({
            integrationKeys: {
                ...formData.integrationKeys,
                [key]: value,
            },
        });
    };

    return (
        <div className="space-y-8">
            {/* Page Header */}
            <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Integration Keys</h2>
                <p className="text-gray-600">
                    Configure payment gateways, notifications, and communication channels
                </p>
            </div>

            {/* Organizer Email - Required */}
            <div className="bg-white border-2 border-blue-200 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                    <div className="p-3 bg-blue-50 rounded-lg">
                        <Mail className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                        <label htmlFor="organizerEmail" className="block text-sm font-medium text-gray-700 mb-2">
                            Organizer Email <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="email"
                            id="organizerEmail"
                            value={formData.integrationKeys.organizerEmail}
                            onChange={(e) => { updateIntegrationKey('organizerEmail', e.target.value); clearError?.('organizerEmail'); }}
                            placeholder="organizer@example.com"
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all ${errors.organizerEmail ? 'border-red-500 focus:ring-red-500 bg-red-50' : 'border-gray-300 focus:ring-blue-500'}`}
                        />
                        {errors.organizerEmail && <p className="text-xs text-red-600 mt-1">{errors.organizerEmail}</p>}
                        <p className="mt-2 text-sm text-gray-500">
                            This email will receive participant registrations and notifications
                        </p>
                    </div>
                </div>
            </div>

            {/* WhatsApp Integration */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                    <div className="p-3 bg-green-50 rounded-lg">
                        <MessageSquare className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                            <label htmlFor="whatsappKey" className="block text-sm font-medium text-gray-700">
                                WhatsApp API Key
                            </label>
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Optional</span>
                        </div>
                        <div className="relative">
                            <input
                                type={showWhatsappKey ? 'text' : 'password'}
                                id="whatsappKey"
                                value={formData.integrationKeys.whatsappKey || ''}
                                onChange={(e) => updateIntegrationKey('whatsappKey', e.target.value)}
                                placeholder="Enter WhatsApp Business API key"
                                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            />
                            <button
                                type="button"
                                onClick={() => setShowWhatsappKey(!showWhatsappKey)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                {showWhatsappKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                        </div>
                        <p className="mt-2 text-sm text-gray-500">
                            Send automated WhatsApp notifications to participants
                        </p>
                    </div>
                </div>
            </div>

            {/* Razorpay Integration - Only show for paid events */}
            {formData.eventType === 'paid' && (
                <div className="space-y-4">
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6">
                        <div className="flex items-start space-x-4 mb-6">
                            <div className="p-3 bg-purple-100 rounded-lg">
                                <CreditCard className="w-6 h-6 text-purple-600" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                    Payment Gateway (Razorpay)
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Required for accepting payments from participants
                                </p>
                            </div>
                        </div>

                        {/* Razorpay Key ID */}
                        <div className="mb-4">
                            <label htmlFor="razorpayKey" className="block text-sm font-medium text-gray-700 mb-2">
                                Razorpay Key ID <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="text"
                                    id="razorpayKey"
                                    value={formData.integrationKeys.razorpayKey || ''}
                                    onChange={(e) => { updateIntegrationKey('razorpayKey', e.target.value); clearError?.('razorpayKey'); }}
                                    placeholder="rzp_test_xxxxxxxxxxxxxxxx"
                                    className={`w-full pl-12 pr-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all ${errors.razorpayKey ? 'border-red-500 focus:ring-red-500 bg-red-50' : 'border-gray-300 focus:ring-blue-500'}`}
                                />
                            </div>
                            {errors.razorpayKey && <p className="text-xs text-red-600 mt-1">{errors.razorpayKey}</p>}
                            <p className="mt-2 text-xs text-gray-500">
                                Found in Razorpay Dashboard → Settings → API Keys
                            </p>
                        </div>

                        {/* Razorpay Secret */}
                        <div>
                            <label htmlFor="razorpaySecret" className="block text-sm font-medium text-gray-700 mb-2">
                                Razorpay Key Secret <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type={showRazorpaySecret ? 'text' : 'password'}
                                    id="razorpaySecret"
                                    value={formData.integrationKeys.razorpaySecret || ''}
                                    onChange={(e) => { updateIntegrationKey('razorpaySecret', e.target.value); clearError?.('razorpaySecret'); }}
                                    placeholder="Enter Razorpay secret key"
                                    className={`w-full pl-12 pr-12 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-all font-mono text-sm ${errors.razorpaySecret ? 'border-red-500 focus:ring-red-500 bg-red-50' : 'border-gray-300 focus:ring-blue-500'}`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowRazorpaySecret(!showRazorpaySecret)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showRazorpaySecret ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                            {errors.razorpaySecret && <p className="text-xs text-red-600 mt-1">{errors.razorpaySecret}</p>}
                            <p className="mt-2 text-xs text-gray-500">
                                Keep this secret secure. It will be encrypted before storage.
                            </p>
                        </div>

                        {/* Razorpay Webhook Secret */}
                        <div>
                            <label htmlFor="razorpayWebhookSecret" className="block text-sm font-medium text-gray-700 mb-2">
                                Razorpay Webhook Secret
                            </label>
                            <div className="relative">
                                <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="text"
                                    id="razorpayWebhookSecret"
                                    value={formData.integrationKeys.razorpayWebhookSecret || ''}
                                    onChange={(e) => updateIntegrationKey('razorpayWebhookSecret', e.target.value)}
                                    placeholder="Enter user defined webhook secret"
                                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all font-mono text-sm"
                                />
                            </div>
                            <p className="mt-2 text-xs text-gray-500">
                                Secret used to verify webhook events from Razorpay.
                            </p>
                        </div>

                        {/* Webhook URL Display */}
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                                Razorpay Webhook URL
                            </label>
                            <div className="flex items-center space-x-2">
                                <code className="flex-1 p-2 bg-white border border-gray-300 rounded text-sm text-gray-600 break-all">
                                    {process.env.REACT_APP_API_URL}/api/v2/payment/webhook
                                </code>
                                <button
                                    type="button"
                                    onClick={() => {
                                        navigator.clipboard.writeText(`${process.env.REACT_APP_API_URL}/api/v2/payment/webhook`);
                                        alert('Copied to clipboard!');
                                    }}
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                    title="Copy to clipboard"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                                    </svg>
                                </button>
                            </div>
                            <p className="mt-2 text-xs text-gray-500">
                                Add this URL in Razorpay Dashboard → Settings → Webhooks
                            </p>
                        </div>
                    </div>

                    {/* Security Notice */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <Shield className="h-5 w-5 text-amber-400" />
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-amber-800">Security Notice</h3>
                                <div className="mt-2 text-sm text-amber-700">
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>All sensitive keys are encrypted using industry-standard encryption</li>
                                        <li>Keys are securely stored and never exposed in logs or responses</li>
                                        <li>Use test keys for testing and live keys for production</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Integration Guide */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                            <path
                                fillRule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">Integration Guide</h3>
                        <div className="mt-2 text-sm text-blue-700 space-y-2">
                            <div>
                                <strong>WhatsApp:</strong> Get API key from{' '}
                                <a href="https://business.whatsapp.com" target="_blank" rel="noopener noreferrer" className="underline">
                                    WhatsApp Business Platform
                                </a>
                            </div>
                            <div>
                                <strong>Razorpay:</strong> Sign up at{' '}
                                <a href="https://razorpay.com" target="_blank" rel="noopener noreferrer" className="underline">
                                    razorpay.com
                                </a>
                                {' '}and get your API keys from the dashboard
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* vCard Invitation */}
            <div className="bg-white border-2 border-indigo-200 rounded-xl p-6">
                <div className="flex items-start space-x-4">
                    <div className="p-3 bg-indigo-50 rounded-lg shrink-0">
                        {/* vCard icon */}
                        <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                        </svg>
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                            <h3 className="text-sm font-semibold text-gray-800">vCard Profile Invitation</h3>
                            <button
                                type="button"
                                onClick={() => updateFormData({ vcardInvite: { ...formData.vcardInvite, enabled: !formData.vcardInvite?.enabled } })}
                                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none ${formData.vcardInvite?.enabled ? 'bg-indigo-600' : 'bg-gray-300'}`}
                            >
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${formData.vcardInvite?.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                            </button>
                        </div>
                        <p className="text-xs text-gray-500 mb-4">
                            When enabled, participants who don't have a vCard profile yet will receive an email invitation to create one after registering.
                        </p>

                        {formData.vcardInvite?.enabled && (
                            <div className="space-y-3">
                                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Send invitation to</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {/* All participants */}
                                    <button
                                        type="button"
                                        onClick={() => updateFormData({ vcardInvite: { ...formData.vcardInvite, mode: 'all' } })}
                                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                                            (formData.vcardInvite?.mode || 'all') === 'all'
                                                ? 'border-indigo-500 bg-indigo-50'
                                                : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                    >
                                        <p className="font-semibold text-gray-900 text-sm">All Participants</p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Every new participant gets an invitation regardless of category.
                                        </p>
                                    </button>

                                    {/* Per category */}
                                    <button
                                        type="button"
                                        onClick={() => updateFormData({ vcardInvite: { ...formData.vcardInvite, mode: 'per-category' } })}
                                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                                            formData.vcardInvite?.mode === 'per-category'
                                                ? 'border-indigo-500 bg-indigo-50'
                                                : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                    >
                                        <p className="font-semibold text-gray-900 text-sm">Per Category</p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Only send to categories you choose. Configure in the Categories step.
                                        </p>
                                    </button>
                                </div>

                                {formData.vcardInvite?.mode === 'per-category' && (
                                    <p className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2">
                                        Go to <strong>Step 4 — Participant Categories</strong> and enable the vCard invite toggle on each category you want.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Quick Test */}
            {formData.eventType === 'paid' && formData.integrationKeys.razorpayKey && (
                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                            <span className="text-sm font-medium text-green-800">
                                Payment gateway configured
                            </span>
                        </div>
                        <button
                            type="button"
                            className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                        >
                            Test Connection
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IntegrationKeysStep;